import { useState, useEffect, useMemo } from 'react'
import {
  Box, Container, Typography, Grid, Card, CardMedia, CardContent,
  CardActions, Button, Chip, TextField, InputAdornment, CircularProgress,
  Alert, Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Paper, Divider
} from '@mui/material'
import {
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  Check as CheckedIcon,
  PhotoCamera as PhotoIcon,
  Palette as PaletteIcon,
  WbSunny as LightIcon,
  AspectRatio as FramingIcon,
  Close as CloseIcon
} from '@mui/icons-material'
import AppHeader from '../components/common/AppHeader'

// #T# StyleLibraryPage — przeglądarka biblioteki stylów fotograficznych
// Dane: GET /stylista/styles/index.json (statyczny JSON w public/)
// Każdy styl: style_id, name, prompt_injection, reference_image, lighting, color, composition

interface StyleEntry {
  style_id: string
  name: string
  created_from: string
  reference_image: string
  prompt_injection: string
  lighting_type: string | null
  color_grade: string | null
  composition_framing: string | null
  camera_aesthetic: string | null
}

interface StyleDetail extends StyleEntry {
  lighting: Record<string, string>
  color: Record<string, unknown>
  composition: Record<string, string>
  subject?: Record<string, string>
  background?: Record<string, string>
}

const COLOR_CHIPS: Record<string, string> = {
  warm: '#e8a030',
  cool: '#4a90d9',
  vibrant: '#e040fb',
  neutral: '#78909c',
  muted: '#8d6e63',
  bw: '#424242',
  pastel: '#f48fb1',
  dark: '#37474f',
  desaturated: '#9e9e9e',
}

const LIGHTING_CHIPS: Record<string, string> = {
  natural: '#66bb6a',
  studio: '#42a5f5',
  outdoor: '#26a69a',
  mixed: '#ab47bc',
}

function StyleCard({ style, onSelect }: { style: StyleEntry; onSelect: (s: StyleEntry) => void }) {
  const [copied, setCopied] = useState(false)
  const [imgError, setImgError] = useState(false)

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(style.prompt_injection || '').then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const colorChip = style.style_id.startsWith('bw_') ? 'bw'
    : style.color_grade || 'neutral'

  return (
    <Card
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer',
            transition: 'transform 0.15s, box-shadow 0.15s',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 } }}
      onClick={() => onSelect(style)}
    >
      {!imgError ? (
        <CardMedia
          component="img"
          height={200}
          image={style.reference_image}
          alt={style.name}
          onError={() => setImgError(true)}
          sx={{ objectFit: 'cover' }}
        />
      ) : (
        <Box sx={{ height: 200, bgcolor: 'grey.200', display: 'flex',
                   alignItems: 'center', justifyContent: 'center' }}>
          <PhotoIcon sx={{ fontSize: 48, color: 'grey.400' }} />
        </Box>
      )}

      <CardContent sx={{ flex: 1, pb: 0 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom noWrap>
          {style.name}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {colorChip && (
            <Chip label={colorChip} size="small"
              sx={{ bgcolor: COLOR_CHIPS[colorChip] || '#78909c', color: '#fff', fontSize: 10, height: 18 }} />
          )}
          {style.lighting_type && (
            <Chip label={style.lighting_type} size="small"
              sx={{ bgcolor: LIGHTING_CHIPS[style.lighting_type] || '#78909c', color: '#fff', fontSize: 10, height: 18 }} />
          )}
          {style.composition_framing && (
            <Chip label={style.composition_framing} size="small" variant="outlined"
              sx={{ fontSize: 10, height: 18 }} />
          )}
        </Box>

        <Typography variant="caption" color="text.secondary"
          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {style.prompt_injection}
        </Typography>
      </CardContent>

      <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
        <Tooltip title={copied ? 'Skopiowano!' : 'Kopiuj prompt injection'}>
          <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
            {copied ? <CheckedIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  )
}

function StyleDetailDialog({ style, open, onClose }: {
  style: StyleDetail | null; open: boolean; onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [imgError, setImgError] = useState(false)

  if (!style) return null

  const handleCopy = () => {
    navigator.clipboard.writeText(style.prompt_injection || '').then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{style.name}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            {!imgError ? (
              <Box component="img" src={style.reference_image} alt={style.name}
                onError={() => setImgError(true)}
                sx={{ width: '100%', borderRadius: 1, objectFit: 'cover', maxHeight: 400 }} />
            ) : (
              <Box sx={{ height: 300, bgcolor: 'grey.100', borderRadius: 1,
                         display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PhotoIcon sx={{ fontSize: 64, color: 'grey.400' }} />
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {style.created_from}
            </Typography>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                PROMPT INJECTION
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {style.prompt_injection}
              </Typography>
            </Paper>

            <Divider sx={{ my: 1.5 }} />

            {style.camera_aesthetic && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Aesthetic</Typography>
                <Typography variant="body2">{style.camera_aesthetic}</Typography>
              </Box>
            )}

            {style.lighting && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <LightIcon fontSize="small" color="action" />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">LIGHTING</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(style.lighting).map(([k, v]) => (
                    <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  ))}
                </Box>
              </Box>
            )}

            {style.color && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <PaletteIcon fontSize="small" color="action" />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">COLOR</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(style.color).filter(([k]) => k !== 'dominant_palette').map(([k, v]) => (
                    <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  ))}
                </Box>
                {Array.isArray(style.color.dominant_palette) && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {(style.color.dominant_palette as string[]).map((hex) => (
                      <Tooltip key={hex} title={hex}>
                        <Box sx={{ width: 24, height: 24, bgcolor: hex, borderRadius: '50%',
                                   border: '1px solid', borderColor: 'divider' }} />
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {style.composition && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <FramingIcon fontSize="small" color="action" />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">COMPOSITION</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(style.composition).map(([k, v]) => (
                    <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  ))}
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCopy} variant="contained" startIcon={copied ? <CheckedIcon /> : <CopyIcon />}
          color={copied ? 'success' : 'primary'}>
          {copied ? 'Skopiowano!' : 'Kopiuj Prompt Injection'}
        </Button>
        <Button onClick={onClose}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const FILTER_OPTIONS = ['all', 'bw', 'warm', 'vibrant', 'cool', 'muted', 'neutral']
const SETTING_OPTIONS = ['all', 'studio_white', 'studio_gray', 'urban', 'nature', 'interior']
const FRAMING_OPTIONS = ['all', 'full body', '3/4 body', 'waist up', 'portrait', 'headshot']

const StyleLibraryPage = () => {
  const [styles, setStyles] = useState<StyleEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterColor, setFilterColor] = useState('all')
  const [filterSetting, setFilterSetting] = useState('all')
  const [filterFraming, setFilterFraming] = useState('all')
  const [selected, setSelected] = useState<StyleDetail | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  useEffect(() => {
    fetch('/stylista/styles/index.json')
      .then(r => r.json())
      .then(setStyles)
      .catch(() => setError('Nie można załadować biblioteki stylów'))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    return styles.filter(s => {
      const idStr = s.style_id || ''
      const colorOk = filterColor === 'all'
        || idStr.startsWith(filterColor + '_')
        || (filterColor === 'bw' && idStr.startsWith('bw_'))
      const settingOk = filterSetting === 'all' || idStr.includes(filterSetting)
      const framingOk = filterFraming === 'all' || s.composition_framing === filterFraming
      const searchOk = !search
        || s.name.toLowerCase().includes(search.toLowerCase())
        || (s.prompt_injection || '').toLowerCase().includes(search.toLowerCase())
        || (s.camera_aesthetic || '').toLowerCase().includes(search.toLowerCase())
      return colorOk && settingOk && framingOk && searchOk
    })
  }, [styles, filterColor, filterSetting, filterFraming, search])

  const handleSelect = async (s: StyleEntry) => {
    try {
      const data = await fetch(`/stylista/styles/${s.style_id}.json`).then(r => r.json())
      setSelected({ ...s, ...data })
      setDetailOpen(true)
    } catch {
      setSelected(s as StyleDetail)
      setDetailOpen(true)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppHeader />
      <Container maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Style Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Biblioteka stylów fotograficznych — kliknij kartę, aby zobaczyć szczegóły i skopiować prompt injection.
          </Typography>
        </Box>

        {/* Filters */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" placeholder="Szukaj stylu lub promptu..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Kolor:</Typography>
                  {FILTER_OPTIONS.map(opt => (
                    <Chip key={opt} label={opt} size="small" clickable
                      onClick={() => setFilterColor(opt)}
                      variant={filterColor === opt ? 'filled' : 'outlined'}
                      sx={{ mr: 0.5, mb: 0.5,
                            ...(filterColor === opt && opt !== 'all' ? { bgcolor: COLOR_CHIPS[opt], color: '#fff' } : {}) }} />
                  ))}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Kadrowanie:</Typography>
                  {FRAMING_OPTIONS.map(opt => (
                    <Chip key={opt} label={opt} size="small" clickable
                      onClick={() => setFilterFraming(opt)}
                      variant={filterFraming === opt ? 'filled' : 'outlined'}
                      sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {filtered.length} z {styles.length} stylów
            </Typography>
            <Grid container spacing={2}>
              {filtered.map(style => (
                <Grid item key={style.style_id} xs={12} sm={6} md={4} lg={3} xl={2}>
                  <StyleCard style={style} onSelect={handleSelect} />
                </Grid>
              ))}
              {filtered.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="info">Brak stylów pasujących do filtrów.</Alert>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Container>

      <StyleDetailDialog style={selected} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </Box>
  )
}

export default StyleLibraryPage
